// BUILD-046 (Owner report 2026-10-22): where the "More actions" menu of a task row is drawn so that it is never cut off.
//
// The task table is `overflow-hidden` with rounded corners, so a menu that hung below its row was clipped wherever it passed the bottom edge of the table (the last rows). The menu is now drawn on the
// page body and placed against its button with fixed coordinates; this module decides the coordinates. It opens below the button, or above it when the room below is too small for the whole menu and there
// is more room above; it is moved sideways to stay inside the window (its right edge lines up with the button's); and when neither side has room for every entry it keeps the larger side and the menu
// scrolls inside itself (`maxHeight`).
//
// Pure module: numbers in, numbers out. Erasable TypeScript only.

export const MENU_MARGIN = 8;
export const MENU_GAP = 4;
/** The least height a menu is given when the window is very short, so at least a few entries show. */
export const MENU_MIN_HEIGHT = 96;

export interface AnchorRect {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export interface MenuPosition {
  top: number;
  left: number;
  /** The tallest the menu may be where it is placed; it scrolls inside itself beyond this. */
  maxHeight: number;
  /** True when the menu opens above the button. */
  above: boolean;
}

/** `menu` is the menu's natural size (its widest line and the height of all its entries); `viewport` is the window. */
export function placeMenu(anchor: AnchorRect, menu: { width: number; height: number }, viewport: { width: number; height: number }): MenuPosition {
  const roomBelow = viewport.height - anchor.bottom - MENU_MARGIN - MENU_GAP;
  const roomAbove = anchor.top - MENU_MARGIN - MENU_GAP;
  const below = menu.height <= roomBelow || roomBelow >= roomAbove;
  const room = Math.max(below ? roomBelow : roomAbove, MENU_MIN_HEIGHT);
  const height = Math.min(menu.height, room);
  const wanted = below ? anchor.bottom + MENU_GAP : anchor.top - MENU_GAP - height;
  const top = Math.max(MENU_MARGIN, Math.min(wanted, viewport.height - MENU_MARGIN - height));
  const left = Math.max(MENU_MARGIN, Math.min(anchor.right - menu.width, viewport.width - menu.width - MENU_MARGIN));
  return { top, left, maxHeight: room, above: !below };
}
